/**
 * Disciplinary page — list actions (severity badges), create/edit (Admin/Manager),
 * and acknowledge (employees acknowledge their own actions).
 *
 * Server scoping: employees see only their own actions; managers/admins see all
 * except their own (separation of duties). Employees get an Acknowledge button
 * for unacknowledged actions.
 */
import { useEffect, useMemo, useState } from 'react';
import { Gavel, Plus, CheckCircle2, Pencil, Trash2, FileDown, Download } from 'lucide-react';
import {
  useDisciplinary,
  useCreateDisciplinary,
  useUpdateDisciplinary,
  useDeleteDisciplinary,
  useAcknowledgeDisciplinary,
} from '../api.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Select, Textarea, Field } from '../../../shell/Field.js';
import { useEmployeeDirectory } from '../../employees/api.js';
import { EmployeeSelect, formatEmployeeOption } from '../../employees/components/EmployeeSelect.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { DetailField, DetailViewModal, ViewRecordButton } from '../../../shell/DetailViewModal.js';
import { formatDate } from '../../../lib/format.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { downloadCsv } from '../../../lib/csv.js';
import { useAuthStore } from '../../../store/auth.js';
import { SEVERITY, type Severity } from '@shared/enums/index.js';
import type { DisciplinaryAction } from '@shared/types';

const severityTone: Record<Severity, 'blue' | 'yellow' | 'red' | 'slate'> = {
  Verbal: 'blue',
  Written: 'yellow',
  Final: 'red',
  Termination: 'slate',
};

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function DisciplinaryPage() {
  const { data, isLoading } = useDisciplinary();
  const { user } = useAuthStore();
  const { data: directory } = useEmployeeDirectory();
  const employeeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of directory?.items ?? []) {
      map.set(e.id, formatEmployeeOption(e));
    }
    return map;
  }, [directory]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DisciplinaryAction | null>(null);
  const [viewing, setViewing] = useState<DisciplinaryAction | null>(null);
  const acknowledge = useAcknowledgeDisciplinary();
  const deleteAction = useDeleteDisciplinary();

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this disciplinary action? This cannot be undone.')) return;
    try {
      await deleteAction.mutateAsync(id);
      toast('success', 'Disciplinary action deleted.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to delete.';
      toast('error', msg);
    }
  };


  const canManage = user?.role === 'Admin' || user?.role === 'Manager';
  const isEmployee = user?.role === 'Employee';

  const handleAcknowledge = async (id: string) => {
    try {
      await acknowledge.mutateAsync(id);
      toast('success', 'Action acknowledged.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to acknowledge.';
      toast('error', msg);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (action: DisciplinaryAction) => {
    setEditing(action);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const columns: Column<DisciplinaryAction>[] = [
    { key: 'date', header: 'Date', render: (d) => formatDate(d.date) },
    {
      key: 'employee',
      header: 'Employee',
      render: (d) => (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {employeeLabel.get(d.employeeId) ?? 'Employee'}
        </span>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (d) => <Badge tone={severityTone[d.severity]}>{d.severity}</Badge>,
    },
    {
      key: 'details',
      header: 'Preview',
      render: (d) => <span className="line-clamp-1 text-slate-600 dark:text-slate-400">{d.details}</span>,
    },
    { key: 'due', header: 'Due', render: (d) => (d.dueDate ? formatDate(d.dueDate) : '—') },
    {
      key: 'acknowledged',
      header: 'Acknowledged',
      render: (d) =>
        d.acknowledgedAt ? (
          <Badge tone="green">Yes</Badge>
        ) : isEmployee ? (
          <Button
            size="sm"
            variant="secondary"
            loading={acknowledge.isPending}
            onClick={(e) => {
              e.stopPropagation();
              handleAcknowledge(d.id);
            }}
          >
            <CheckCircle2 className="h-4 w-4" /> Acknowledge
          </Button>
        ) : (
          <Badge tone="slate">No</Badge>
        ),
    },
  ];

  columns.push({
    key: 'actions',
    header: '',
    render: (d) => (
      <div className="flex justify-end gap-1">
        <ViewRecordButton
          label={`${d.severity} action`}
          onClick={() => setViewing(d)}
        />
        <a
          href={`/api/action-pdfs/disciplinary/${d.id}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <Button size="sm" variant="ghost" aria-label="Download PDF">
            <FileDown className="h-4 w-4" />
          </Button>
        </a>
        {canManage && (
          <>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Edit action"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(d);
              }}
            >
              <Pencil className="h-4 w-4" /> Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Delete action"
              loading={deleteAction.isPending}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(d.id);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    ),
  });

  const handleExport = () => {
    downloadCsv(
      `disciplinary-${new Date().toISOString().slice(0, 10)}.csv`,
      (data?.items ?? []).map((d) => ({
        Employee: employeeLabel.get(d.employeeId) ?? d.employeeId,
        Date: formatDate(d.date),
        Severity: d.severity,
        Details: d.details,
        DueDate: d.dueDate ? formatDate(d.dueDate) : '',
        Acknowledged: d.acknowledgedAt ? 'Yes' : 'No',
        IssuedBy: employeeLabel.get(d.issuedById) ?? '',
      })),
    );
    toast('success', 'CSV downloaded.');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Disciplinary Actions</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{data?.items.length ?? 0} actions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleExport} disabled={!data?.items.length}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          {canManage && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" /> New Action
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
      ) : data && data.items.length > 0 ? (
        <Table columns={columns} data={data.items} />
      ) : (
        <EmptyState
          icon={<Gavel className="h-8 w-8" />}
          title="No disciplinary actions"
          description={canManage ? 'Record a new action to get started.' : 'Actions concerning you will appear here.'}
        />
      )}

      {canManage && (
        <ActionModal open={modalOpen} onClose={closeModal} action={editing} />
      )}

      <DisciplinaryViewModal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        action={viewing}
        employeeLabel={
          viewing ? (employeeLabel.get(viewing.employeeId) ?? 'Employee') : ''
        }
      />
    </div>
  );
}

function DisciplinaryViewModal({
  open,
  onClose,
  action,
  employeeLabel,
}: {
  open: boolean;
  onClose: () => void;
  action: DisciplinaryAction | null;
  employeeLabel: string;
}) {
  if (!action) return null;

  return (
    <DetailViewModal open={open} onClose={onClose} title={`${action.severity} action`}>
      <DetailField label="Date">{formatDate(action.date)}</DetailField>
      <DetailField label="Employee">{employeeLabel}</DetailField>
      <DetailField label="Severity">{action.severity}</DetailField>
      <DetailField label="Details">{action.details}</DetailField>
      {action.actionPlan ? (
        <DetailField label="Action plan">{action.actionPlan}</DetailField>
      ) : null}
      {action.dueDate ? (
        <DetailField label="Due date">{formatDate(action.dueDate)}</DetailField>
      ) : null}
      {action.acknowledgedAt ? (
        <DetailField label="Acknowledged">{formatDate(action.acknowledgedAt)}</DetailField>
      ) : null}
    </DetailViewModal>
  );
}

function ActionModal({
  open,
  onClose,
  action,
}: {
  open: boolean;
  onClose: () => void;
  /** When provided, modal is in edit mode. */
  action?: DisciplinaryAction | null;
}) {
  const create = useCreateDisciplinary();


  const update = useUpdateDisciplinary();
  const { user } = useAuthStore();
  const { data: directory } = useEmployeeDirectory();
  const employeeName = useMemo(() => {
    if (!action?.employeeId) return '';
    const match = directory?.items.find((e) => e.id === action.employeeId);
    return match ? formatEmployeeOption(match) : 'Employee';
  }, [action?.employeeId, directory]);
  const isEdit = Boolean(action);
  const today = new Date().toISOString().slice(0, 10);
  const [employeeId, setEmployeeId] = useState('');
  const [date, setDate] = useState(today);
  const [severity, setSeverity] = useState<Severity>('Verbal');
  const [details, setDetails] = useState('');
  const [actionPlan, setActionPlan] = useState('');
  const [dueDate, setDueDate] = useState('');

  const reset = () => {
    setEmployeeId('');
    setDate(today);
    setSeverity('Verbal');
    setDetails('');
    setActionPlan('');
    setDueDate('');
  };

  useEffect(() => {
    if (!open) return;
    if (action) {
      setEmployeeId(action.employeeId);
      setDate(toDateInput(action.date) || today);
      setSeverity(action.severity);
      setDetails(action.details);
      setActionPlan(action.actionPlan ?? '');
      setDueDate(toDateInput(action.dueDate));
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset uses today; re-run on open/action
  }, [open, action]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const body = {
      date: new Date(`${date}T00:00:00.000Z`).toISOString(),
      severity,
      details,
      actionPlan,
      dueDate: dueDate ? new Date(`${dueDate}T00:00:00.000Z`).toISOString() : null,
    };

    try {
      if (isEdit && action) {
        await update.mutateAsync({ id: action.id, body });
        toast('success', 'Action updated.');
      } else {
        await create.mutateAsync({
          employeeId,
          ...body,
        });
        toast('success', 'Action recorded.');
      }
      reset();
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiRequestError
          ? err.body.message
          : isEdit
            ? 'Failed to update action.'
            : 'Failed to create action.';
      toast('error', msg);
    }
  };

  const pending = create.isPending || update.isPending;
  const canSubmit = isEdit ? Boolean(date && details) : Boolean(employeeId && date && details);

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? 'Edit Disciplinary Action' : 'New Disciplinary Action'}>
      <div className="space-y-4">
        {!isEdit && (
          <EmployeeSelect
            label="Employee"
            value={employeeId}
            onChange={setEmployeeId}
            excludeIds={user?.employeeId ? [user.employeeId] : []}
          />
        )}
        {isEdit && (
          <Field label="Employee">
            <p className="text-sm text-slate-700 dark:text-slate-300">{employeeName}</p>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Select
            label="Severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as Severity)}
          >
            {SEVERITY.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>
        </div>
        <Textarea
          label="Details"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={3}
          placeholder="What happened…"
        />
        <Textarea
          label="Action plan"
          value={actionPlan}
          onChange={(e) => setActionPlan(e.target.value)}
          rows={2}
          placeholder="Corrective steps…"
        />
        <Field label="Due date (optional)">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          <Button
            type="button"
            onClick={handleSubmit}
            loading={pending}
            disabled={!canSubmit}
          >
            {isEdit ? 'Save Changes' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
